// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023-2024 LABELOPS.ai Corporation
//
// SPDX-License-Identifier: MIT

import LABELOPSCore from 'labelops-core/src';
import _labelops from 'labelops-core/src/api';

import ObjectState from 'labelops-core/src/object-state';
import Webhook from 'labelops-core/src/webhook';
import MLModel from 'labelops-core/src/ml-model';
import CloudStorage from 'labelops-core/src/cloud-storage';
import { ModelProvider } from 'labelops-core/src/lambda-manager';
import {
    Label, Attribute,
} from 'labelops-core/src/labels';
import {
    SerializedAttribute, SerializedLabel, SerializedAPISchema,
} from 'labelops-core/src/server-response-types';
import { Job, Task } from 'labelops-core/src/session';
import Project from 'labelops-core/src/project';
import QualityReport, { QualitySummary } from 'labelops-core/src/quality-report';
import QualityConflict, { AnnotationConflict, ConflictSeverity } from 'labelops-core/src/quality-conflict';
import QualitySettings from 'labelops-core/src/quality-settings';
import { FramesMetaData, FrameData } from 'labelops-core/src/frames';
import { ServerError, RequestError } from 'labelops-core/src/exceptions';
import {
    ShapeType, LabelType, ModelKind, ModelProviders,
    ModelReturnType, DimensionType, JobType,
    JobStage, JobState, RQStatus,
} from 'labelops-core/src/enums';
import { Storage, StorageData } from 'labelops-core/src/storage';
import Issue from 'labelops-core/src/issue';
import Comment from 'labelops-core/src/comment';
import User from 'labelops-core/src/user';
import Organization, { Membership, Invitation } from 'labelops-core/src/organization';
import AnnotationGuide from 'labelops-core/src/guide';
import AnalyticsReport, { AnalyticsEntryViewType, AnalyticsEntry } from 'labelops-core/src/analytics-report';
import { Dumper } from 'labelops-core/src/annotation-formats';
import { Event } from 'labelops-core/src/event';
import { APIWrapperEnterOptions } from 'labelops-core/src/plugins';
import BaseSingleFrameAction, { ActionParameterType, FrameSelectionType } from 'labelops-core/src/annotations-actions';
import { Request } from 'labelops-core/src/request';

const labelops: LABELOPSCore = _labelops;

labelops.config.backendAPI = '/api';
labelops.config.origin = window.location.origin;
// Set the TUS chunk size to 2 MB. A small value works better in case of a slow internet connection.
// A larger value may cause a server-side timeout errors in the current implementation.
labelops.config.uploadChunkSize = 2;
(globalThis as any).labelops = labelops;

function getCore(): typeof labelops {
    return labelops;
}

type ProjectOrTaskOrJob = Project | Task | Job;

export {
    getCore,
    ObjectState,
    Label,
    Job,
    Task,
    Project,
    AnnotationGuide,
    Attribute,
    ShapeType,
    LabelType,
    Storage,
    Webhook,
    Issue,
    User,
    CloudStorage,
    Organization,
    Membership,
    Invitation,
    Comment,
    MLModel,
    ModelKind,
    ModelProviders,
    ModelReturnType,
    DimensionType,
    Dumper,
    JobType,
    JobStage,
    JobState,
    RQStatus,
    BaseSingleFrameAction,
    QualityReport,
    QualityConflict,
    QualitySettings,
    AnnotationConflict,
    ConflictSeverity,
    FramesMetaData,
    AnalyticsReport,
    AnalyticsEntry,
    AnalyticsEntryViewType,
    ServerError,
    RequestError,
    Event,
    FrameData,
    ActionParameterType,
    FrameSelectionType,
    Request,
};

export type {
    SerializedAttribute,
    SerializedLabel,
    StorageData,
    ModelProvider,
    APIWrapperEnterOptions,
    QualitySummary,
    LABELOPSCore,
    SerializedAPISchema,
    ProjectOrTaskOrJob,
};
